import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Bike as BikeIcon, CloudOff, Loader2 } from "lucide-react";
import { api, CATEGORIES, CATEGORY_LABELS, euro, imageUri, SIZES } from "@/lib/noleggio";
import { useI18n } from "@/lib/i18n";
import ShopContact from "@/components/ShopContact";
import GoogleRating from "@/components/GoogleRating";

export default function Catalog() {
  const { t } = useI18n();
  const [cat, setCat] = useState("all");
  const q = useQuery({ queryKey: ["bikes", cat], queryFn: () => api(`/bikes${cat === "all" ? "" : `?category=${cat}`}`) });

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 pt-8">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-6 items-end [&>*]:min-w-0">
        <div>
          <p className="text-xs font-bold tracking-[0.14em] uppercase text-[var(--brand)]">{t("kicker")}</p>
          <h1 data-testid="catalog-title" className="text-4xl md:text-5xl font-extrabold tracking-tight mt-2">{t("choose_bike")}</h1>
          <p className="text-[var(--text-2)] mt-3 max-w-lg">{t("hero_text")}</p>
          <GoogleRating />
        </div>
        <ShopContact prefix="home" />
      </div>

      <div className="mn-hscroll mt-8">
        {["all", ...CATEGORIES].map((c) => (
          <button key={c} data-testid={`category-chip-${c}`} onClick={() => setCat(c)} className={`mn-chip ${cat === c ? "mn-chip-on" : ""}`}>
            {c === "all" ? t("all") : CATEGORY_LABELS[c]}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <div data-testid="catalog-loading" className="py-24 flex flex-col items-center gap-3 text-[var(--muted)]">
          <Loader2 className="animate-spin" color="var(--brand-primary)" /> {t("loading_bikes")}
        </div>
      ) : q.isError ? (
        <div className="py-24 flex flex-col items-center gap-4 text-[var(--muted)]">
          <CloudOff size={40} /> {t("load_error")}
          <button data-testid="catalog-retry-button" className="mn-btn mn-btn-ghost" onClick={() => q.refetch()}>{t("retry")}</button>
        </div>
      ) : (q.data ?? []).length === 0 ? (
        <div className="py-24 flex flex-col items-center gap-3 text-[var(--muted)]"><BikeIcon size={44} /> {t("no_bikes")}</div>
      ) : (
        <div data-testid="bike-list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {q.data.map((b, i) => <BikeCard key={b.id} bike={b} index={i} />)}
        </div>
      )}
    </div>
  );
}

function BikeCard({ bike, index }) {
  const { t } = useI18n();
  const total = SIZES.reduce((a, s) => a + (bike.stock?.[s] ?? 0), 0);
  return (
    <Link to={`/bici/${bike.id}`} data-testid={`bike-card-${bike.id}`} className="mn-card mn-bike-card mn-rise block overflow-hidden" style={{ animationDelay: `${index * 70}ms` }}>
      <div className="relative h-56 bg-[var(--surface-3)] overflow-hidden">
        {bike.image_url ? (
          <img src={imageUri(bike.image_url)} alt={bike.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--muted)]"><BikeIcon size={64} /></div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/75 to-transparent" />
        <div className="absolute left-4 right-4 bottom-3.5 text-white">
          <p className="text-xs font-bold uppercase tracking-wider opacity-90">{CATEGORY_LABELS[bike.category]}</p>
          <p className="text-xl font-extrabold truncate">{bike.name}</p>
        </div>
      </div>
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {SIZES.map((s) => {
            const off = !(bike.stock?.[s] > 0);
            return (
              <span key={s} className={`w-8 h-7 rounded-md text-xs font-bold flex items-center justify-center ${off ? "bg-[var(--surface-2)] text-[var(--muted)]" : "bg-[var(--brand-tertiary)] text-[var(--brand-primary)]"}`}>{s}</span>
            );
          })}
          <span className="text-xs text-[var(--muted)] ml-1">{t("in_fleet", { n: total })}</span>
        </div>
        <p data-testid={`bike-price-day-${bike.id}`} className="text-lg font-extrabold whitespace-nowrap">
          {euro(bike.price_day)}<span className="text-xs font-medium text-[var(--muted)]">{t("per_day")}</span>
        </p>
      </div>
    </Link>
  );
}
